import { faroCoreApiClient } from "@api/faro-core-api-client";
import { getCompanyContext } from "@api/core-api-utils";
import { createAsyncThunk } from "@reduxjs/toolkit";
/**
 * Fetch list of companies and workspaces of the user
 * Companies are fetched with user role
 * Workspaces are all companies that the user has access to plus the admin panel access
 * @throws {HttpUtils.FetchError} if any Core API client request fails
 */
export const fetchCompaniesAndWorkspaces = createAsyncThunk("workspace/fetchCompaniesAndWorkspaces", async () => {
  const [fetchedCompanies, fetchedWorkspaces] = await Promise.all([faroCoreApiClient.company.getCompanies(), faroCoreApiClient.users.getCurrentUsersWorkspaces()]);
  return {
    companies: fetchedCompanies,
    workspaces: fetchedWorkspaces?.workspaces ?? []
  };
});

/**
 * Fetches the branding settings of the company from the backend
 * @throws {Error} if there is no selected company ID
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const fetchCompanyBrandingSettings = createAsyncThunk("sdbCompany/fetchCompanyBrandingSettings", async (_, {
  getState
}) => {
  const {
    sdbCompany: {
      selectedSdbCompanyId
    }
  } = getState();
  if (!selectedSdbCompanyId) {
    throw new Error("No companyId was given to fetchCompanyBrandingSettings");
  }
  return await faroCoreApiClient.company.getBrandingSettings({
    companyId: selectedSdbCompanyId
  });
});

/**
 * Updates the branding settings of the company
 * @throws {Error} if there is no selected company ID
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const updateCompanyBrandingSettings = createAsyncThunk("sdbCompany/updateCompanyBrandingSettings", async ({
  payload
}, {
  getState
}) => {
  const {
    sdbCompany: {
      selectedSdbCompanyId
    }
  } = getState();
  if (!selectedSdbCompanyId) {
    throw new Error("No companyId was given to updateCompanyBrandingSettings");
  }
  await faroCoreApiClient.company.updateBrandingSettings({
    companyId: selectedSdbCompanyId,
    payload
  });

  // Returning the payload because the response does not contain any information on the settings that were changed
  return payload;
});

/**
 * Fetches company context from the backend
 * @throws {Error} if there is no selected company ID
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const fetchCompanyContext = createAsyncThunk("sdbCompany/fetchCompanyContext", async (_, {
  getState
}) => {
  const {
    sdbCompany: {
      selectedSdbCompanyId
    },
    app: {
      isDevModeEnabled
    }
  } = getState();
  if (!selectedSdbCompanyId) {
    throw new Error("No companyId was given to fetchCompanyContext");
  }
  return await getCompanyContext(selectedSdbCompanyId, isDevModeEnabled);
});

/**
 * Fetches the communication settings of the company from the backend.
 * This includes the language to communicate with company and whether they are using Sphere XG as default app or not
 * @throws {Error} if there is no selected company ID
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const fetchCompanyCommunicationSettings = createAsyncThunk("sdbCompany/fetchCompanyCommunicationSettings", async (_, {
  getState
}) => {
  const {
    sdbCompany: {
      selectedSdbCompanyId
    }
  } = getState();
  if (!selectedSdbCompanyId) {
    throw new Error("No companyId was given to fetchCompanyBrandingSettings");
  }
  return await faroCoreApiClient.company.getCompanyCommunicationSettings({
    companyId: selectedSdbCompanyId
  });
}, {
  condition: (_, api) => {
    // Skip fetching if communication settings are already available in the store
    return !api.getState().sdbCompany.communicationSettings;
  }
});

/**
 * Updates the company communication settings of the company.
 * This includes the language to communicate with company and whether they are using Sphere XG as default app or not
 * @throws {Error} if there is no selected company ID
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const updateCompanyCommunicationSettings = createAsyncThunk("sdbCompany/updateCompanyCommunicationSettings", async ({
  payload
}, {
  getState
}) => {
  const {
    sdbCompany: {
      selectedSdbCompanyId
    }
  } = getState();
  if (!selectedSdbCompanyId) {
    throw new Error("No companyId was given to updateCompanyCommunicationSettings");
  }
  await faroCoreApiClient.company.updateCompanyCommunicationSettings({
    companyId: selectedSdbCompanyId,
    payload
  });

  // Returning the payload because the response does not contain any information on the settings that were changed
  return payload;
});

/**
 * Fetches all subscriptions of the company from the backend
 * @throws {Error} if there is no selected company ID
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const fetchCompanySubscriptions = createAsyncThunk("sdbCompany/fetchCompanySubscriptions", async ({
  queryOptions
}, {
  getState
}) => {
  const {
    sdbCompany: {
      selectedSdbCompanyId
    }
  } = getState();
  if (!selectedSdbCompanyId) {
    throw new Error("No companyId was given to fetchCompanySubscriptions");
  }
  return await faroCoreApiClient.company.getCompanySubscriptions({
    companyId: selectedSdbCompanyId,
    options: queryOptions
  });
});